import React, { useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';

import { tokens } from '@hotjar/hotsauce';
import { PROGRESS_HANDLE_Z_INDEX } from './constants';
import { useRealTimeStore } from '../store/realTimeStore';

const HANDLE_SIZE = 18;

const HandleDiv = styled.div`
  position: absolute;
  left: 0;
  z-index: ${PROGRESS_HANDLE_Z_INDEX};
`;

const CircleDiv = styled.div`
  cursor: pointer;
  height: ${HANDLE_SIZE}px;
  width: ${HANDLE_SIZE}px;
  border-radius: 50%;
  background: ${tokens.color.ember_600};
  ${tokens.elevation(2)};

  transition: all 0.1s ease-out;
`;

interface Props {
  visible: boolean;
  width: number;
  duration: number;
  onMouseDown: (clientX: number, uiPosition: string) => void;
}

export const ProgressHandleUnmemo = ({
  width,
  duration,
  visible,
  onMouseDown,
}: Props): JSX.Element => {
  const elementDivRef = useRef<HTMLDivElement>(null);

  // This useLayoutEffect updates "style.transform" only when activeTime changes
  useLayoutEffect(() => {
    return useRealTimeStore.subscribe(
      (activeTime: number) => {
        if (!elementDivRef.current) {
          return;
        }
        const xPositionInPx = (activeTime / duration) * width;
        elementDivRef.current.style.transform = `translateX(${xPositionInPx - HANDLE_SIZE / 2}px)`;
      },
      (state) => (state.isDragging ? state.draggedTime : state.currentTimeWithLessPrecision),
    );
  }, [elementDivRef, duration, width]);

  // This useLayoutEffect updates "style.transform" only when duration or width change (usefull when we resize or switch to fullscreen)
  useLayoutEffect(() => {
    if (!elementDivRef.current) {
      return;
    }
    const xPositionInPx =
      (useRealTimeStore.getState().currentTimeWithLessPrecision / duration) * width;
    elementDivRef.current.style.transform = `translateX(${xPositionInPx - HANDLE_SIZE / 2}px)`;
  }, [duration, width]);

  return (
    <HandleDiv
      onMouseDown={(e) => onMouseDown(e.clientX, 'dragged_timeline_wheel')}
      draggable="false"
      data-testid="progress-handle"
      ref={elementDivRef}
    >
      <CircleDiv
        style={{
          transform: `scale(${visible ? 1 : 0})`,
        }}
      />
    </HandleDiv>
  );
};

export const ProgressHandle = React.memo(ProgressHandleUnmemo);
